// Connection Credibility — own breakdown + improvement tips.
// Routed at /settings/credibility (also linked from main Settings).

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ShieldCheck, RefreshCw, Info } from 'lucide-react';
import { api } from '@/contexts/AuthContext';
import { CredibilityBadge } from '../components/CredibilityBadge';

export default function Credibility() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    try {
      const r = await api.get('/credibility/me');
      setData(r.data);
    } catch (_) { /* swallow */ }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const refresh = async () => {
    setRefreshing(true);
    try {
      const r = await api.post('/credibility/me/refresh');
      setData(r.data);
    } catch (_) {}
    finally { setRefreshing(false); }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-[#8A8A93]">Loading…</div>;
  }
  if (!data) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-3">
        <p className="text-[#8A8A93]">Could not load your credibility score.</p>
        <button onClick={load} className="px-4 py-2 rounded-full bg-[#B91C1C] text-white text-sm font-semibold">Retry</button>
      </div>
    );
  }

  const tier = data.tier;
  const calibrated = data.calibrated;

  return (
    <div className="min-h-screen bg-[var(--surface)]" data-testid="credibility-page">
      {/* Header */}
      <div className="sticky top-0 bg-white/95 backdrop-blur-md border-b border-[var(--hairline)] z-10">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="w-9 h-9 rounded-full border border-black/10 flex items-center justify-center hover:bg-black/[0.04] transition"
            data-testid="back-btn"
          >
            <ArrowLeft size={16} />
          </button>
          <h1 className="text-[15px] font-bold text-[#0F0F10] flex-1">Connection Credibility</h1>
          <button
            onClick={refresh}
            disabled={refreshing}
            className="w-9 h-9 rounded-full border border-black/10 flex items-center justify-center hover:bg-black/[0.04] transition disabled:opacity-40"
            data-testid="refresh-btn"
            title="Recalculate"
          >
            <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-6 space-y-5">
        {/* Hero card */}
        <div
          className="rounded-3xl p-6 border-2 text-center"
          style={{ backgroundColor: `${tier.color}0F`, borderColor: `${tier.color}33` }}
          data-testid="cred-hero"
        >
          <div className="flex items-center justify-center mb-2">
            <ShieldCheck size={36} strokeWidth={2} style={{ color: tier.color }} />
          </div>
          <p className="text-[10px] uppercase tracking-[0.16em] font-bold" style={{ color: tier.color }}>
            {tier.label}
          </p>
          <p className="text-[56px] leading-none font-bold tabular-nums mt-1" style={{ color: tier.color }}>
            {calibrated ? data.score : '—'}
          </p>
          <p className="text-[11px] text-[#8A8A93] mt-1">
            {calibrated ? 'out of 100' : 'Calibrating — keep replying to your matches'}
          </p>
          {!calibrated && (
            <p className="text-[12px] text-[#4B4B52] mt-3 max-w-sm mx-auto">
              Your score becomes real after 5 message exchanges. Until then, everyone starts neutral so brand-new daters aren't penalized.
            </p>
          )}
        </div>

        {/* Breakdown */}
        <div className="bg-white rounded-2xl border border-[var(--hairline)] overflow-hidden" data-testid="cred-breakdown">
          <p className="px-4 pt-3 text-[10px] uppercase tracking-[0.16em] font-bold text-[#8A8A93]">Breakdown</p>
          <div className="divide-y divide-[var(--hairline)]">
            {data.breakdown.map((row, i) => (
              <div key={i} className="px-4 py-3 flex items-start gap-3">
                <div className="flex-1">
                  <p className="text-[13px] font-semibold text-[#0F0F10]">{row.label}</p>
                  <p className="text-[11px] text-[#8A8A93] mt-0.5">{row.hint}</p>
                </div>
                <p className="text-[14px] tabular-nums font-bold text-[#0F0F10]">{row.value}</p>
              </div>
            ))}
          </div>
        </div>

        {/* How to improve */}
        <div className="bg-white rounded-2xl border border-[var(--hairline)] p-5">
          <p className="text-[10px] uppercase tracking-[0.16em] font-bold text-[#8A8A93] mb-2">How to climb tiers</p>
          <ul className="space-y-2.5 text-[13px] text-[#3F3F46]">
            <li className="flex gap-2"><span className="text-[#15803D]">✓</span> Reply within 7 days when someone messages you (+ to reply rate)</li>
            <li className="flex gap-2"><span className="text-[#15803D]">✓</span> Faster median responses (under 4h) earn up to <b>+10</b> bonus</li>
            <li className="flex gap-2"><span className="text-[#15803D]">✓</span> Use <b>Politely Pass</b> instead of going silent — soft +2 each (capped at 10)</li>
            <li className="flex gap-2"><span className="text-[#B91C1C]">✗</span> Going silent within 7 days is <b>−5 per ghost</b></li>
            <li className="flex gap-2"><span className="text-[#B91C1C]">✗</span> Dropping an active conversation (5+ msgs) is <b>−8 per drop</b></li>
            <li className="flex gap-2"><span className="text-[#B91C1C]">✗</span> Blocking someone right after they message you is <b>−10 per silent unmatch</b></li>
          </ul>
          <div className="mt-4 p-3 bg-[#FAFAFA] rounded-lg flex gap-2 text-[11px] text-[#52525B]">
            <Info size={13} className="flex-shrink-0 mt-0.5" />
            <span>Higher score = priority visibility in Discover. Chronic ghosters are filtered out for new viewers, so they're harder to match with — but not banned.</span>
          </div>
        </div>

        {/* Public visibility */}
        <div className="text-center text-[11px] text-[#8A8A93]">
          Your score and tier are visible on your public profile.
          <CredibilityBadge credibility={{ score: data.score, tier, calibrated }} size="md" className="ml-2" />
        </div>
      </div>
    </div>
  );
}
